import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts';
import { GET_IMAGE_HEALTH_SCORE } from '../graphql/queries';

function gradeColor(grade) {
  const map = { A: '#00b894', B: '#00cec9', C: '#fdcb6e', D: '#e17055', F: '#d63031' };
  return map[grade] || '#636e72';
}

function ScoreGauge({ score, grade }) {
  const data = [{ value: score, fill: gradeColor(grade) }];
  return (
    <div style={{ width: 200, height: 200, position: 'relative', margin: '0 auto' }}>
      <ResponsiveContainer>
        <RadialBarChart innerRadius="70%" outerRadius="100%" data={data} startAngle={180} endAngle={0} barSize={16}>
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar background clockWise dataKey="value" angleAxisId={0} cornerRadius={8} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -30%)', textAlign: 'center' }}>
        <div style={{ fontSize: 36, fontWeight: 700, color: gradeColor(grade) }}>{grade}</div>
        <div style={{ fontSize: 14, color: '#636e72' }}>{score}/100</div>
      </div>
    </div>
  );
}

function SubScoreCard({ label, score, weight, detail }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 16 }}>
      <div style={{ fontSize: 12, color: '#636e72', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: score >= 80 ? '#00b894' : score >= 60 ? '#fdcb6e' : '#d63031' }}>
        {score}
      </div>
      <div style={{ fontSize: 11, color: '#b2bec3', marginTop: 4 }}>Weight: {Math.round(weight * 100)}%</div>
      {detail && <div style={{ fontSize: 11, color: '#636e72', marginTop: 4 }}>{detail}</div>}
    </div>
  );
}

export default function HealthScorecard() {
  const { imageId } = useParams();
  const { data, loading, error } = useQuery(GET_IMAGE_HEALTH_SCORE, { variables: { imageId } });

  if (loading) return <div className="loading">Calculating health score...</div>;
  if (error) return <div className="error">Failed to calculate health score: {error.message}</div>;

  const score = data?.getImageHealthScore;
  if (!score) return <div className="error">No health data available for this image</div>;

  const { breakdown } = score;

  return (
    <div>
      <h2 className="page-title">Health Scorecard: {score.imageName}</h2>

      <div className="card" style={{ textAlign: 'center' }}>
        <ScoreGauge score={score.overallScore} grade={score.grade} />
        <p style={{ marginTop: 8, color: '#636e72' }}>
          Overall health score for image <Link to={`/images/${imageId}`} className="link">{score.imageName}</Link>
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        <SubScoreCard
          label="CVE Score"
          score={score.cveScore}
          weight={breakdown.cveWeight}
          detail={`${breakdown.criticalCveCount} critical, ${breakdown.highCveCount} high`}
        />
        <SubScoreCard
          label="Age Score"
          score={score.ageScore}
          weight={breakdown.ageWeight}
          detail={`${breakdown.imageAgeDays} days old`}
        />
        <SubScoreCard
          label="CIS Score"
          score={score.cisScore}
          weight={breakdown.cisWeight}
          detail={`${breakdown.cisPassed} passed, ${breakdown.cisFailed} failed`}
        />
        <SubScoreCard
          label="SBOM Score"
          score={score.sbomScore}
          weight={breakdown.sbomWeight}
          detail={breakdown.hasSbom ? 'SBOM present' : 'No SBOM'}
        />
        <SubScoreCard
          label="Signing Score"
          score={score.signingScore}
          weight={breakdown.signingWeight}
          detail={breakdown.isSigned ? 'Signed' : 'Unsigned'}
        />
      </div>

      {score.recommendations?.length > 0 && (
        <div className="card">
          <h3>Recommendations</h3>
          <ul style={{ paddingLeft: 20, marginTop: 8 }}>
            {score.recommendations.map((rec, i) => (
              <li key={i} style={{ marginBottom: 6, color: '#636e72' }}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="actions">
        <Link to={`/images/${imageId}`} className="btn btn-primary">View Image Details</Link>
        <Link to={`/compliance/${imageId}`} className="btn btn-primary" style={{ background: '#6c5ce7' }}>Compliance Report</Link>
        <Link to={`/sbom/${imageId}`} className="btn btn-primary" style={{ background: '#0984e3' }}>View SBOM</Link>
      </div>
    </div>
  );
}
